import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import DateRangeFilter from '../../components/DateRangeFilter';

const StatisticsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calls, setCalls] = useState([]);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [minutesByDate, setMinutesByDate] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [isLoadingAssistants, setIsLoadingAssistants] = useState(true);
  const [showMissedCalls, setShowMissedCalls] = useState(false);
  
  // Optimized data from server-side functions
  const [callStats, setCallStats] = useState({
    total_calls: 0,
    total_minutes: 0,
    successful_calls: 0,
    failed_calls: 0,
    missed_calls: 0
  });
  const [durationDistribution, setDurationDistribution] = useState([]);
  const [ratingsDistribution, setRatingsDistribution] = useState([]);
  const [statusByTime, setStatusByTime] = useState([]);
  const [endReasonsByTime, setEndReasonsByTime] = useState([]);
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [activeFilter, setActiveFilter] = useState('today');

  // Predefined filter functions
  const applyToday = () => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    
    setStartDate(start);
    setEndDate(end);
    setActiveFilter('today');
  };

  const applyLast7Days = () => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
    
    setStartDate(start);
    setEndDate(end);
    setActiveFilter('last7days');
  };

  const applyThisMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    
    setStartDate(start);
    setEndDate(end);
    setActiveFilter('thismonth');
  };

  const applyCustom = () => {
    setActiveFilter('custom');
  };

  // Handle manual date changes to switch to custom mode
  const handleDateChange = (newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setActiveFilter('custom');
  };

  // Define fetchCalls and fetchAnalytics functions first
  

  const fetchAnalytics = useCallback(async () => {
    if (!startDate || !endDate || !selectedAssistant) {
      return;
    }

    try {
      // Fix date handling for same-day ranges
      // Create proper start and end of day in local timezone
      const startOfDay = new Date(startDate);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      
      console.log('Analytics date range:', {
        startOfDay: startOfDay.toISOString(),
        endOfDay: endOfDay.toISOString(),
        selectedAssistant
      });
      
      // Fetch statistics using optimized RPC function
      const { data: statsData, error: statsError } = await supabase
        .rpc('get_call_statistics', {
          p_assistant_id: selectedAssistant,
          p_start_date: startOfDay.toISOString(),
          p_end_date: endOfDay.toISOString()
        });

      if (statsError) {
        console.error('Supabase stats error:', statsError);
        throw new Error(`Failed to fetch statistics: ${statsError.message}`);
      }

      if (statsData && statsData.length > 0) {
        const stats = statsData[0];
        console.log('Statistics data:', stats);
        setCallStats(stats);
        setTotalMinutes(Number(stats.total_minutes.toFixed(2)));
      } else {
        setCallStats({
          total_calls: 0,
          total_minutes: 0,
          successful_calls: 0,
          failed_calls: 0,
          missed_calls: 0
        });
        setTotalMinutes(0);
      }

      // Fetch all optimized data in parallel
      const [
        { data: minutesData, error: minutesError },
        { data: durationData, error: durationError },
        { data: ratingsData, error: ratingsError },
        { data: statusData, error: statusError },
        { data: endReasonsData, error: endReasonsError }
      ] = await Promise.all([
        supabase.rpc('get_minutes_by_date', {
          p_assistant_id: selectedAssistant,
          p_start_date: startOfDay.toISOString(),
          p_end_date: endOfDay.toISOString()
        }),
        supabase.rpc('get_call_duration_distribution', {
          p_assistant_id: selectedAssistant,
          p_start_date: startOfDay.toISOString(),
          p_end_date: endOfDay.toISOString()
        }),
        supabase.rpc('get_call_ratings_distribution', {
          p_assistant_id: selectedAssistant,
          p_start_date: startOfDay.toISOString(),
          p_end_date: endOfDay.toISOString()
        }),
        supabase.rpc('get_call_status_by_time', {
          p_assistant_id: selectedAssistant,
          p_start_date: startOfDay.toISOString(),
          p_end_date: endOfDay.toISOString()
        }),
        supabase.rpc('get_end_reasons_by_time', {
          p_assistant_id: selectedAssistant,
          p_start_date: startOfDay.toISOString(),
          p_end_date: endOfDay.toISOString()
        })
      ]);

      // Handle errors
      if (minutesError) {
        console.error('Supabase minutes error:', minutesError);
        throw new Error(`Failed to fetch minutes data: ${minutesError.message}`);
      }
      if (durationError) {
        console.error('Supabase duration error:', durationError);
        throw new Error(`Failed to fetch duration data: ${durationError.message}`);
      }
      if (ratingsError) {
        console.error('Supabase ratings error:', ratingsError);
        throw new Error(`Failed to fetch ratings data: ${ratingsError.message}`);
      }
      if (statusError) {
        console.error('Supabase status error:', statusError);
        throw new Error(`Failed to fetch status data: ${statusError.message}`);
      }
      if (endReasonsError) {
        console.error('Supabase end reasons error:', endReasonsError);
        throw new Error(`Failed to fetch end reasons data: ${endReasonsError.message}`);
      }

      // Set all the optimized data
      console.log('Minutes by date data:', minutesData);
      setMinutesByDate(minutesData || []);
      
      console.log('Duration distribution data:', durationData);
      setDurationDistribution(durationData || []);
      
      console.log('Ratings distribution data:', ratingsData);
      setRatingsDistribution(ratingsData || []);
      
      console.log('Status by time data:', statusData);
      setStatusByTime(statusData || []);
      
      console.log('End reasons by time data:', endReasonsData);
      setEndReasonsByTime(endReasonsData || []);
      
      // Debug: Log the processed data
      if (endReasonsData && endReasonsData.length > 0) {
        console.log('Processed end reasons data:', endReasonsData.map(item => ({
          timeSlot: item.time_slot,
          'Customer did not answer': item.customer_did_not_answer,
          'Call ended normally': item.call_ended_normally,
          'Silence timeout': item.silence_timeout,
          'Technical error': item.technical_error,
          'Unknown': item.unknown,
          total: item.total
        })));
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message);
      setTotalMinutes(0);
      setMinutesByDate([]);
      setCallStats({
        total_calls: 0,
        total_minutes: 0,
        successful_calls: 0,
        failed_calls: 0,
        missed_calls: 0
      });
      setDurationDistribution([]);
      setRatingsDistribution([]);
      setStatusByTime([]);
      setEndReasonsByTime([]);
    }
  }, [startDate, endDate, selectedAssistant]);

  // Fetch assistants from Supabase
  useEffect(() => {
    const fetchAssistants = async () => {
      setIsLoadingAssistants(true);
      setError(null);
      setAssistants([]); // Reset assistants array

      try {
        console.log('Fetching user...');
        // Get the current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) {
          console.error('Supabase auth error:', userError);
          throw new Error(`Authentication error: ${userError.message}`);
        }
        if (!user) {
          throw new Error('Not authenticated. Please log in.');
        }
        console.log('User found:', user.id);

        // First get the user's organization memberships
        console.log('Fetching organization memberships...');
        const { data: memberships, error: membershipsError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id);

        if (membershipsError) {
          console.error('Supabase memberships error:', membershipsError);
          throw new Error(`Failed to fetch organization memberships: ${membershipsError.message}`);
        }
        
        console.log('Organization memberships response:', memberships);
        
        if (!memberships || memberships.length === 0) {
          throw new Error('No organization found. Please contact your administrator.');
        }

        // Get all assistants for the organization
        console.log('Fetching assistants for organization:', memberships[0].organization_id);
        const { data: assistantsData, error: assistantsError } = await supabase
          .from('organization_assistants')
          .select(`
            assistant_id,
            organizations (
              name
            )
          `)
          .eq('organization_id', memberships[0].organization_id);

        if (assistantsError) {
          console.error('Supabase assistants error:', assistantsError);
          throw new Error(`Failed to fetch assistants: ${assistantsError.message}`);
        }

        console.log('Assistants response:', assistantsData);

        if (!assistantsData || assistantsData.length === 0) {
          throw new Error('No assistants found for your organization.');
        }

        // Map the assistants data
        const assistantsList = assistantsData.map(a => ({
          id: a.assistant_id,
          organizationName: a.organizations?.name || 'Unknown Organization'
        }));

        console.log('Processed assistants list:', assistantsList);
        setAssistants(assistantsList);
        if (assistantsList.length > 0) {
          setSelectedAssistant(assistantsList[0].id);
        }
      } catch (error) {
        console.error('Error in fetchAssistants:', error);
        setError(error.message || 'Failed to load assistants');
        setAssistants([]); // Clear assistants on error
      } finally {
        setIsLoadingAssistants(false);
      }
    };

    fetchAssistants();
  }, []);



  const fetchCalls = useCallback(async () => {
    if (!startDate || !endDate || !selectedAssistant) {
      return; 
    }

    setIsLoading(true);
    setError(null);
    try {
      // Fix date handling for same-day ranges
      // Create proper start and end of day in local timezone
      const startOfDay = new Date(startDate);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      
      console.log('Calls date range:', {
        startOfDay: startOfDay.toISOString(),
        endOfDay: endOfDay.toISOString(),
        selectedAssistant
      });
      
      // Fetch calls from Supabase (still need raw data for some charts)
      const { data: callsData, error: callsError } = await supabase
        .from('calls')
        .select(`
          id,
          vapi_call_id,
          assistant_id,
          created_at_vapi,
          started_at_vapi,
          ended_at_vapi,
          analysis,
          ended_reason
        `)
        .eq('assistant_id', selectedAssistant)
        .gte('created_at_vapi', startOfDay.toISOString())
        .lte('created_at_vapi', endOfDay.toISOString());

      if (callsError) {
        console.error('Supabase calls error:', callsError);
        throw new Error(`Failed to fetch calls: ${callsError.message}`);
      }

      console.log('Calls query result:', {
        totalRows: callsData?.length || 0,
        selectedAssistant
      });
      
      setCalls(callsData || []);

    } catch (e) {
      console.error("Failed to fetch calls:", e);
      setError(e.message);
      setCalls([]);
    }
    setIsLoading(false);
  }, [startDate, endDate, selectedAssistant]);

  // useEffect to call fetchCalls and fetchAnalytics when dependencies change
  useEffect(() => {
    if (!startDate || !endDate || !selectedAssistant) {
      return;
    }
    fetchCalls();
    fetchAnalytics();
  }, [startDate, endDate, selectedAssistant, fetchCalls, fetchAnalytics]);

  const processSuccessRate = () => {
    // Use server-side data if available, otherwise fall back to client-side processing
    const successCount = callStats.successful_calls || calls.filter(call => call.analysis?.successEvaluation === 'true').length;
    const failureCount = callStats.failed_calls || calls.filter(call => call.analysis?.successEvaluation === 'false').length;
    const missedCallCount = callStats.missed_calls || (calls.length - successCount - failureCount);

    const data = [
      { name: 'Successful', value: successCount },
      { name: 'Failed', value: failureCount }
    ];

    // Only include missed calls if toggle is enabled
    if (showMissedCalls) {
      data.push({ name: 'Missed Call', value: missedCallCount });
    }

    return data;
  };

  const processCallDurations = () => {
    // Use server-side data if available, otherwise fall back to client-side processing
    if (durationDistribution.length > 0) {
      return durationDistribution;
    }

    const durationRanges = {
      '0-1 min': 0,
      '1-2 min': 0,
      '2-5 min': 0,
      '5+ min': 0
    };

    calls.forEach(call => {
      const startTime = call.started_at_vapi ?? call.created_at_vapi;
      if (startTime && call.ended_at_vapi) {
        const durationInMinutes = (new Date(call.ended_at_vapi) - new Date(startTime)) / 1000 / 60; // in minutes
        if (durationInMinutes <= 1) durationRanges['0-1 min']++;
        else if (durationInMinutes <= 2) durationRanges['1-2 min']++;
        else if (durationInMinutes <= 5) durationRanges['2-5 min']++;
        else durationRanges['5+ min']++;
      }
    });

    return Object.entries(durationRanges).map(([range, count]) => ({
      range,
      calls: count
    }));
  };

  const processRatings = () => {
    // Use server-side data if available, otherwise fall back to client-side processing
    if (ratingsDistribution.length > 0) {
      // Process server-side data to round down decimal ratings
      const roundedData = {};
      
      ratingsDistribution.forEach(item => {
        // Extract the rating number from "X Stars" format
        const ratingMatch = item.name.match(/(\d+(?:\.\d+)?)\s+Stars/);
        if (ratingMatch) {
          const originalRating = parseFloat(ratingMatch[1]);
          const roundedRating = Math.floor(originalRating);
          const roundedKey = `${roundedRating} Stars`;
          
          if (roundedData[roundedKey]) {
            roundedData[roundedKey].value += item.value;
            // Recalculate percentage
            const total = Object.values(roundedData).reduce((sum, item) => sum + item.value, 0);
            roundedData[roundedKey].percentage = Math.round((roundedData[roundedKey].value / total) * 100);
          } else {
            roundedData[roundedKey] = {
              name: roundedKey,
              value: item.value,
              percentage: item.percentage
            };
          }
        }
      });
      
      // Recalculate all percentages based on the merged data
      const totalCalls = Object.values(roundedData).reduce((sum, item) => sum + item.value, 0);
      Object.values(roundedData).forEach(item => {
        item.percentage = totalCalls > 0 ? Math.round((item.value / totalCalls) * 100) : 0;
      });
      
      return Object.values(roundedData);
    }

    const ratingCounts = {
      '0': 0,
      '1': 0,
      '2': 0,
      '3': 0,
      '4': 0,
      '5': 0,
      'No Rating': 0
    };

    calls.forEach(call => {
      if (call.analysis?.structuredData?.rating !== undefined) {
        const rating = call.analysis.structuredData.rating;
        if (rating >= 0 && rating <= 5) {
          // Round down decimal ratings (4.5 becomes 4, 3.7 becomes 3, etc.)
          const roundedRating = Math.floor(rating);
          ratingCounts[roundedRating.toString()]++;
        }
      } else {
        ratingCounts['No Rating']++;
      }
    });

    const totalRatedCalls = calls.length - ratingCounts['No Rating'];
    
    return Object.entries(ratingCounts)
      .filter(([rating]) => rating !== 'No Rating')
      .map(([rating, count]) => ({
        name: `${rating} Stars`,
        value: count,
        percentage: totalRatedCalls > 0 ? Math.round(count / totalRatedCalls * 100) : 0
      }));
  };

  const processCallStatusByTime = () => {
    // Use server-side data if available, otherwise fall back to client-side processing
    if (statusByTime.length > 0) {
      return statusByTime;
    }

    console.log('Processing call status by time with', calls.length, 'calls');
    const timeSlots = {};
    // Initialize time slots from 10 AM to 11 PM
    for (let hour = 10; hour <= 23; hour++) {
      const timeLabel = `${hour}:00`;
      timeSlots[timeLabel] = { success: 0, failure: 0, missedCall: 0 };
    }

    calls.forEach(call => {
      if (call.created_at_vapi) {
        const hour = new Date(call.created_at_vapi).getHours();
        if (hour >= 10 && hour <= 23) {
          const timeLabel = `${hour}:00`;
          if (call.analysis?.successEvaluation === 'true') {
            timeSlots[timeLabel].success++;
          } else if (call.analysis?.successEvaluation === 'false') {
            timeSlots[timeLabel].failure++;
          } else {
            timeSlots[timeLabel].missedCall++;
          }
        }
      } else {
        console.log('Call missing created_at_vapi for status:', call);
      }
    });

    const result = Object.entries(timeSlots).map(([timeSlot, counts]) => ({
      timeSlot,
      ...counts,
      total: counts.success + counts.failure + counts.missedCall
    }));
    console.log('Call status by time result:', result);
    return result;
  };

  const processEndReasonsByTime = () => {
    // Use server-side data if available, otherwise fall back to client-side processing
    if (endReasonsByTime.length > 0) {
      // Map server-side data to the expected format
      return endReasonsByTime.map(item => ({
        timeSlot: item.time_slot,
        'Customer did not answer': item.customer_did_not_answer,
        'Call ended normally': item.call_ended_normally,
        'Silence timeout': item.silence_timeout,
        'Technical error': item.technical_error,
        'Unknown': item.unknown,
        total: item.total
      }));
    }

    const timeSlots = {};
    // Initialize time slots from 10 AM to 11 PM
    for (let hour = 10; hour <= 23; hour++) {
      const timeLabel = `${hour}:00`;
      timeSlots[timeLabel] = {
        'Customer did not answer': 0,
        'Call ended normally': 0,
        'Silence timeout': 0,
        'Technical error': 0,
        'Unknown': 0
      };
    }

    calls.forEach(call => {
      if (call.created_at_vapi) {
        const hour = new Date(call.created_at_vapi).getHours();
        if (hour >= 10 && hour <= 23) {
          const timeLabel = `${hour}:00`;
          const groupedReason = getGroupedEndReason(call.ended_reason);
          timeSlots[timeLabel][groupedReason]++;
        }
      }
    });

    return Object.entries(timeSlots).map(([timeSlot, counts]) => ({
      timeSlot,
      ...counts,
      total: Object.values(counts).reduce((sum, count) => sum + count, 0)
    }));
  };

  const getGroupedEndReason = (reason) => {
    if (!reason) return 'Unknown';
    
    // Customer did not answer
    if (reason === 'customer-busy' || reason === 'customer-did-not-answer') {
      return 'Customer did not answer';
    }
    
    // Call Ended Normally
    if (reason === 'customer-ended-call' || reason === 'assistant-ended-call') {
      return 'Call ended normally';
    }
    
    // Silence Timeout
    if (reason === 'silence-timeout') {
      return 'Silence timeout';
    }
    
    // Technical Error
    if (reason.includes('call.in-progress.error')) {
      return 'Technical error';
    }
    
    return reason;
  };

  const COLORS = ['#4CAF50', '#f44336', '#9e9e9e']; // Success, Failed, Missed Call
  const RATING_COLORS = ['#FFC107', '#FF9800', '#FF5722', '#F44336', '#E91E63', '#9C27B0'];

  if (isLoadingAssistants) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#6C63FF]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 md:p-6 2xl:p-10">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </div>
    );
  }

  if (!assistants.length) {
    return (
      <div className="p-4 md:p-6 2xl:p-10">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline"> No assistants available.</span>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#6C63FF]"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 2xl:p-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-2">
          <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
            Call Statistics
          </h1>
          
          {/* Predefined Filter Buttons */}
          <div className="flex gap-2">
            <button
              onClick={applyToday}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                activeFilter === 'today'
                  ? 'bg-[#6C63FF] text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Today
            </button>
            <button
              onClick={applyLast7Days}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                activeFilter === 'last7days'
                  ? 'bg-[#6C63FF] text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Last 7 days
            </button>
            <button
              onClick={applyThisMonth}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                activeFilter === 'thismonth'
                  ? 'bg-[#6C63FF] text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              This month
            </button>
            <button
              onClick={applyCustom}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                activeFilter === 'custom'
                  ? 'bg-[#6C63FF] text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Custom
            </button>
          </div>
          
          <DateRangeFilter
            startDate={startDate}
            setStartDate={(date) => handleDateChange(date, endDate)}
            endDate={endDate}
            setEndDate={(date) => handleDateChange(startDate, date)}
          />
          {assistants.length > 1 && (
            <select
              value={selectedAssistant || ''}
              onChange={(e) => setSelectedAssistant(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-boxdark text-gray-700 dark:text-gray-300"
            >
              {assistants.map((assistant) => (
                <option key={assistant.id} value={assistant.id}>
                  {assistant.organizationName} - Assistant {assistant.id.substring(0, 8)}...
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Total Minutes and Calls Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">

         {/* Total Calls Card */}
         <div className="bg-white dark:bg-boxdark p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">Total Calls</h2>
          <p className="text-3xl font-bold text-[#6C63FF]">{callStats.total_calls || calls.length || 0} calls</p>
        </div>

        {/* Total Minutes Card */}
        <div className="bg-white dark:bg-boxdark p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">Total Call Minutes</h2>
          <p className="text-3xl font-bold text-[#6C63FF]">{totalMinutes || 0} minutes</p>
        </div>
       
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Minutes Over Time */}
        <div className="bg-white dark:bg-boxdark p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Minutes Over Time</h2>
          <div className="h-[300px]">
            {minutesByDate && minutesByDate.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={minutesByDate}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#666' }}
                    tickLine={{ stroke: '#666' }}
                  />
                  <YAxis 
                    tick={{ fill: '#666' }}
                    tickLine={{ stroke: '#666' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff',
                      border: '1px solid #ccc'
                    }}
                    formatter={(value) => [`${value} minutes`, 'Duration']}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="minutes" 
                    stroke="#6C63FF" 
                    name="Minutes"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500 dark:text-gray-400">No data available for the selected date range</p>
              </div>
            )}
          </div>
        </div>

        {/* Success Rate */}
        <div className="bg-white dark:bg-boxdark p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Call Success Rate</h2>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 dark:text-gray-400">Show Missed Calls</label>
              <button
                onClick={() => setShowMissedCalls(!showMissedCalls)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#6C63FF] focus:ring-offset-2 ${
                  showMissedCalls ? 'bg-[#6C63FF]' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showMissedCalls ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
          <div className="h-[300px]">
            {(callStats.total_calls > 0 || calls.length > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={processSuccessRate()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {processSuccessRate().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500 dark:text-gray-400">No data available for the selected date range</p>
              </div>
            )}
          </div>
        </div>

        {/* Call Durations */}
        <div className="bg-white dark:bg-boxdark p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Call Duration Distribution</h2>
          <div className="h-[300px]">
            {(callStats.total_calls > 0 || calls.length > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={processCallDurations()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="range" 
                    tick={{ fill: '#666' }}
                    tickLine={{ stroke: '#666' }}
                  />
                  <YAxis 
                    tick={{ fill: '#666' }}
                    tickLine={{ stroke: '#666' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff',
                      border: '1px solid #ccc'
                    }}
                    formatter={(value) => [`${value} calls`, 'Count']}
                  />
                  <Legend />
                  <Bar 
                    dataKey="calls" 
                    fill="#6C63FF"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500 dark:text-gray-400">No data available for the selected date range</p>
              </div>
            )}
          </div>
        </div>

        {/* Rating Distribution */}
        <div className="bg-white dark:bg-boxdark p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Call Ratings Distribution</h2>
          <div className="h-[300px]">
            {(callStats.total_calls > 0 || calls.length > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={processRatings()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percentage, value }) => {
                      // Only show labels for slices with >5% or >0 calls
                      const shouldShowLabel = percentage > 5 || value > 0;
                      return shouldShowLabel ? `${name} (${Math.round(percentage)}%)` : '';
                    }}
                  >
                    {processRatings().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={RATING_COLORS[index % RATING_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, props) => {
                      const percentage = props.payload.percentage;
                      return [`${value} calls (${Math.round(percentage)}%)`, name];
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500 dark:text-gray-400">No data available for the selected date range</p>
              </div>
            )}
          </div>
        </div>

        {/* Call Status by Time of Day */}
        <div className="bg-white dark:bg-boxdark p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Call Status by Time of Day</h2>
          <div className="h-[300px]">
            {(callStats.total_calls > 0 || calls.length > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={processCallStatusByTime()}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="timeSlot" 
                    tick={{ fill: '#666' }}
                    tickLine={{ stroke: '#666' }}
                  />
                  <YAxis 
                    tick={{ fill: '#666' }}
                    tickLine={{ stroke: '#666' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff',
                      border: '1px solid #ccc'
                    }}
                    formatter={(value, name) => {
                      if (name === 'success') return [`${value} calls`, 'Successful'];
                      if (name === 'failure') return [`${value} calls`, 'Failed'];
                      if (name === 'missedCall') return [`${value} calls`, 'Missed Call'];
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="success" stackId="a" fill="#4CAF50" name="Successful" />
                  <Bar dataKey="failure" stackId="a" fill="#f44336" name="Failed" />
                  <Bar dataKey="missedCall" stackId="a" fill="#9e9e9e" name="Missed Call" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500 dark:text-gray-400">No data available for the selected date range</p>
              </div>
            )}
          </div>
        </div>

        {/* End Reasons by Time of Day */}
        <div className="bg-white dark:bg-boxdark p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">End Reasons by Time of Day</h2>
          <div className="h-[300px]">
            {(callStats.total_calls > 0 || calls.length > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={processEndReasonsByTime()}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="timeSlot" 
                    tick={{ fill: '#666' }}
                    tickLine={{ stroke: '#666' }}
                  />
                  <YAxis 
                    tick={{ fill: '#666' }}
                    tickLine={{ stroke: '#666' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff',
                      border: '1px solid #ccc'
                    }}
                    formatter={(value, name) => [`${value} calls`, name]}
                  />
                  <Legend />
                  <Bar dataKey="Customer did not answer" stackId="a" fill="#4CAF50" />
                  <Bar dataKey="Call ended normally" stackId="a" fill="#2196F3" />
                  <Bar dataKey="Silence timeout" stackId="a" fill="#FFC107" />
                  <Bar dataKey="Technical error" stackId="a" fill="#f44336" />
                  <Bar dataKey="Unknown" stackId="a" fill="#9e9e9e" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500 dark:text-gray-400">No data available for the selected date range</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPage; 